import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { fitStructureFrame, separateStructureLabels, STRUCTURE_PRODUCT_CENTER } from './structureCalloutLayout.mjs'

const clamp = THREE.MathUtils.clamp
const blue = '#075dd5'

function roundedRect(width, height, radius = .06) {
  const shape = new THREE.Shape()
  const x = -width / 2, y = -height / 2
  shape.moveTo(x + radius, y)
  shape.lineTo(x + width - radius, y)
  shape.quadraticCurveTo(x + width, y, x + width, y + radius)
  shape.lineTo(x + width, y + height - radius)
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  shape.lineTo(x + radius, y + height)
  shape.quadraticCurveTo(x, y + height, x, y + height - radius)
  shape.lineTo(x, y + radius)
  shape.quadraticCurveTo(x, y, x + radius, y)
  return shape
}

function hole(width, height, x = 0, y = 0, radius = .04) {
  const points = roundedRect(width, height, radius).getPoints(12).map(point => new THREE.Vector2(point.x + x, point.y + y))
  return new THREE.Path(points)
}

function createThermostat(product) {
  const root = new THREE.Group()
  const parts = [new THREE.Group(), new THREE.Group(), new THREE.Group()]
  const lineMaterial = new LineMaterial({ color: blue, linewidth: 1.5 })
  const materials = [lineMaterial]
  const geometries = []
  function outline(group, edges) {
    const geometry = new LineSegmentsGeometry().setPositions(edges.attributes.position.array)
    group.add(new LineSegments2(geometry, lineMaterial))
    geometries.push(geometry)
  }
  function solid(group, shape, thickness, fill) {
    const geometry = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false, curveSegments: 12, steps: 1 })
    geometry.rotateX(-Math.PI / 2)
    const material = new THREE.MeshBasicMaterial({ color: fill, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 })
    const mesh = new THREE.Mesh(geometry, material)
    const edges = new THREE.EdgesGeometry(geometry, 24)
    group.add(mesh)
    outline(group, edges)
    // Smooth rounded corners need explicit side silhouettes for the fixed camera.
    const perimeter = shape.getPoints(12)
    for (const side of [-1, 1]) {
      const edge = perimeter.reduce((best, point) => side * (11 * point.x + 7 * point.y) > side * (11 * best.x + 7 * best.y) ? point : best)
      line(group, [[edge.x, 0, -edge.y], [edge.x, thickness, -edge.y]])
      group.children.at(-1).name = 'solid-side'
    }
    materials.push(material)
    geometries.push(geometry, edges)
  }
  function line(group, points, closed = false) {
    const segments = []
    for (let index = 1; index < points.length; index++) segments.push(...points[index - 1], ...points[index])
    if (closed) segments.push(...points[points.length - 1], ...points[0])
    const geometry = new LineSegmentsGeometry().setPositions(segments)
    group.add(new LineSegments2(geometry, lineMaterial))
    geometries.push(geometry)
  }
  function circle(group, x, z, radius, y = .13) {
    line(group, Array.from({ length: 48 }, (_, i) => {
      const angle = i / 48 * Math.PI * 2
      return [x + Math.cos(angle) * radius, y, z + Math.sin(angle) * radius]
    }), true)
  }
  function cylinder(group, radius, height, x = 0, y = 0, z = 0, open = false) {
    const geometry = new THREE.CylinderGeometry(radius, radius, height, 64, 1, open)
    geometry.translate(x, y, z)
    const material = new THREE.MeshBasicMaterial({ color: '#f0f6fd', side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 })
    const edges = new THREE.EdgesGeometry(geometry, 24)
    group.add(new THREE.Mesh(geometry, material))
    outline(group, edges)
    geometries.push(geometry, edges)
    materials.push(material)
  }
  function board(group, width, depth, includeChip = true) {
    solid(group, roundedRect(width, depth, .04), .035, '#e4eefb')
    const chip = new THREE.Group()
    chip.position.y = .04
    solid(chip, roundedRect(width * .32, depth * .3, .025), .065, '#dbeaff')
    if (includeChip) group.add(chip)
    for (const x of [-.22, -.08, .08, .22]) {
      line(group, [[x, .042, -.24], [x, .042, -.38]])
      line(group, [[x, .042, .24], [x, .042, .38]])
    }
    line(group, [[-.3, .042, 0], [-width * .4, .042, 0], [-width * .4, .042, depth * .36], [width * .35, .042, depth * .36]])
  }
  function cylinderSides(group, radius, bottom, top, x, z, viewTangent = [11, -7]) {
    const tangent = new THREE.Vector2(...viewTangent).normalize().multiplyScalar(radius)
    for (const side of [-1, 1]) line(group, [
      [x + side * tangent.x, bottom, z + side * tangent.y],
      [x + side * tangent.x, top, z + side * tangent.y],
    ])
  }
  function thread(group, radius, bottom, top, turns, x = 0, z = 0) {
    const steps = turns * 32
    line(group, Array.from({ length: steps + 1 }, (_, index) => {
      const progress = index / steps, angle = progress * turns * Math.PI * 2
      return [x + Math.cos(angle) * radius, bottom + progress * (top - bottom), z + Math.sin(angle) * radius]
    }))
    group.children.at(-1).name = 'thread'
  }
  function screw(group, x, z, panel = false) {
    const shaftRadius = panel ? .032 : .027, shaftHeight = panel ? .12 : .15
    const shaftY = panel ? -.005 : -.04, headRadius = panel ? .075 : .065
    const headHeight = panel ? .035 : .03, headY = panel ? .0725 : .05
    const bottom = shaftY - shaftHeight / 2, shoulder = shaftY + shaftHeight / 2
    const top = headY + headHeight / 2
    cylinder(group, shaftRadius, shaftHeight, x, shaftY, z)
    cylinderSides(group, shaftRadius, bottom, shoulder, x, z)
    thread(group, shaftRadius + .001, bottom + .006, shoulder - .004, 4, x, z)
    cylinder(group, headRadius, headHeight, x, headY, z)
    cylinderSides(group, headRadius, shoulder, top, x, z)
    const slot = panel ? .04 : .035
    line(group, [[x - slot, top + .002, z], [x + slot, top + .002, z]])
    line(group, [[x, top + .002, z - slot], [x, top + .002, z + slot]])
  }
  if (product === 'trv') {
    const mainBoard = new THREE.Group(), chip = new THREE.Group(), innerBody = new THREE.Group(), screws = new THREE.Group()
    parts.push(mainBoard, chip, innerBody, screws)
    cylinder(parts[0], .92, .28, 0, .14)
    cylinderSides(parts[0], .92, 0, .28, 0, 0)
    circle(parts[0], 0, 0, .75, .285)
    const bodyHeight = 1.4 * 5 / 3
    const bodyExtension = bodyHeight - 1.4
    cylinder(parts[1], .88, bodyHeight, 0, -.3 - bodyExtension / 2, 0, true)
    const shellMesh = parts[1].children.find(child => child.isMesh && !child.isLineSegments2)
    const shellVertices = [], shellIndices = []
    const windowAngle = Math.asin(.525 / .88)
    const angles = [...Array.from({ length: 129 }, (_, index) => index / 128 * Math.PI * 2), windowAngle, Math.PI * 2 - windowAngle].sort((a, b) => a - b)
    for (let index = 0; index < angles.length - 1; index++) {
      const a = angles[index], b = angles[index + 1]
      const inWindow = Math.cos((a + b) / 2) > Math.cos(windowAngle)
      const spans = inWindow ? [[.4 - bodyHeight, -.825 - bodyExtension / 2], [.525 - bodyExtension / 2, .4]] : [[.4 - bodyHeight, .4]]
      for (const [bottom, top] of spans) {
        const start = shellVertices.length / 3
        for (const [angle, y] of [[a, bottom], [b, bottom], [a, top], [b, top]]) shellVertices.push(.88 * Math.sin(angle), y, .88 * Math.cos(angle))
        shellIndices.push(start, start + 1, start + 2, start + 1, start + 3, start + 2)
      }
    }
    const shellGeometry = new THREE.BufferGeometry()
    shellGeometry.setAttribute('position', new THREE.Float32BufferAttribute(shellVertices, 3))
    shellGeometry.setIndex(shellIndices)
    geometries.push(shellGeometry)
    shellMesh.geometry = shellGeometry
    shellMesh.name = 'trv-outer-shell'
    // Smooth cylinder edges omit silhouettes; add the two tangents for the fixed camera view.
    const tangent = new THREE.Vector2(11, -7).normalize().multiplyScalar(.88)
    for (const side of [-1, 1]) line(parts[1], [
      [tangent.x * side, .4, tangent.y * side],
      [tangent.x * side, .4 - bodyHeight, tangent.y * side],
    ])
    const display = new THREE.Group()
    display.name = 'trv-inner-led-display'
    const displayRadius = .746, displayWidth = .94, displayHeight = 1.35, cornerRadius = .06
    const vertices = [], indices = [], topEdge = [], bottomEdge = []
    // Narrow strips keep the curved screen on the cylinder rather than cutting across it.
    for (let column = 0; column <= 64; column++) {
      const x = (column / 64 - .5) * displayWidth
      const cornerX = Math.max(0, Math.abs(x) - (displayWidth / 2 - cornerRadius))
      const halfHeight = displayHeight / 2 - cornerRadius + Math.sqrt(Math.max(0, cornerRadius ** 2 - cornerX ** 2))
      const z = Math.sqrt(displayRadius ** 2 - x ** 2)
      const top = [x, halfHeight, z], bottom = [x, -halfHeight, z]
      vertices.push(...top, ...bottom)
      topEdge.push(top)
      bottomEdge.push(bottom)
      if (column < 64) {
        const offset = column * 2
        indices.push(offset, offset + 1, offset + 2, offset + 1, offset + 3, offset + 2)
      }
    }
    const displayGeometry = new THREE.BufferGeometry()
    displayGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))
    displayGeometry.setIndex(indices)
    const displayMaterial = new THREE.MeshBasicMaterial({ color: '#edf4fc', side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 })
    display.add(new THREE.Mesh(displayGeometry, displayMaterial))
    line(display, [...topEdge, ...bottomEdge.toReversed()], true)
    geometries.push(displayGeometry)
    materials.push(displayMaterial)
    display.position.y = -.15 - bodyExtension / 2
    const windowOutline = [...topEdge, ...bottomEdge.toReversed()].map(([x, y]) => {
      const outerX = x * 1.05 / displayWidth
      return [outerX, y + display.position.y, Math.sqrt(.886 ** 2 - outerX ** 2)]
    })
    line(parts[1], windowOutline, true)
    innerBody.name = 'trv-inner-plastic-body'
    cylinder(innerBody, .74, bodyHeight - .14, 0, -.3 - bodyExtension / 2)
    cylinderSides(innerBody, .74, .47 - bodyHeight, .33, 0, 0)
    innerBody.add(display)
    const reading = new THREE.Group()
    reading.name = 'trv-led-reading-23c'
    display.add(reading)
    function curvedGlyph(points, closed = false) {
      const sampled = []
      for (let segment = 1; segment < points.length; segment++) {
        for (let step = 0; step <= 12; step++) {
          const x = THREE.MathUtils.lerp(points[segment - 1][0], points[segment][0], step / 12)
          const y = THREE.MathUtils.lerp(points[segment - 1][1], points[segment][1], step / 12)
          sampled.push([x, y, Math.sqrt(.75 ** 2 - x ** 2)])
        }
      }
      line(reading, sampled, closed)
    }
    curvedGlyph([[-.33, .23], [-.13, .23], [-.13, .015], [-.33, .015], [-.33, -.2], [-.13, -.2]])
    curvedGlyph([[-.04, .23], [.16, .23], [.16, -.2], [-.04, -.2]])
    curvedGlyph([[-.04, .015], [.16, .015]])
    curvedGlyph(Array.from({ length: 33 }, (_, index) => {
      const angle = index / 32 * Math.PI * 2
      return [.245 + Math.cos(angle) * .026, .24 + Math.sin(angle) * .026]
    }), true)
    curvedGlyph(Array.from({ length: 33 }, (_, index) => {
      const angle = Math.PI / 4 + index / 32 * Math.PI * 1.5
      return [.345 + Math.cos(angle) * .052, .188 + Math.sin(angle) * .052]
    }))
    for (const y of [-.85, -.76]) circle(parts[1], 0, 0, .89, y - bodyExtension)
    const baseY = .4 - bodyHeight - .06
    parts[2].name = 'trv-base-and-thread'
    const baseDisk = new THREE.Group()
    baseDisk.name = 'trv-base-disk'
    baseDisk.position.y = baseY - .06
    const diskShape = new THREE.Shape()
    diskShape.absarc(0, 0, .85, 0, Math.PI * 2, false)
    const centerHole = new THREE.Path()
    centerHole.absarc(0, 0, .12, 0, Math.PI * 2, true)
    diskShape.holes.push(centerHole)
    solid(baseDisk, diskShape, .12, '#f0f6fd')
    parts[2].add(baseDisk)
    cylinder(parts[2], .57, .36, 0, baseY - .24, 0, true)
    cylinderSides(parts[2], .57, baseY - .42, baseY - .06, 0, 0)
    thread(parts[2], .578, baseY - .4, baseY - .08, 4)
    circle(parts[2], 0, 0, .44, baseY - .422)
    board(mainBoard, 1.18, 1.08, false)
    for (const [x, z] of [[-.46, -.4], [.46, -.4], [-.46, .4], [.46, .4]]) {
      const fastener = new THREE.Group()
      fastener.name = 'trv-board-screw'
      fastener.position.y = .035
      screw(fastener, x, z)
      screws.add(fastener)
    }
    chip.name = 'trv-chip'
    solid(chip, roundedRect(.46, .72, .025), .08, '#dbeaff')
    for (const pin of [-.26, -.13, 0, .13, .26]) {
      for (const side of [-1, 1]) {
        line(chip, [[side * .23, .05, pin], [side * .29, .05, pin], [side * .34, .015, pin], [side * .39, .015, pin]])
        chip.children.at(-1).name = 'chip-pin'
      }
    }
  } else if (product === 'timer') {
    const mainBoard = new THREE.Group(), screws = new THREE.Group(), battery = new THREE.Group(), solarPanel = new THREE.Group()
    parts.push(mainBoard, screws, battery, solarPanel)
    solid(parts[0], roundedRect(2.25, 2.35, .24), .12, '#fbfcfe')
    const dropButton = new THREE.Path()
    dropButton.moveTo(0, .24)
    dropButton.bezierCurveTo(-.05, .14, -.16, .035, -.16, -.08)
    dropButton.absarc(0, -.08, .16, Math.PI, Math.PI * 2, false)
    dropButton.bezierCurveTo(.16, .035, .05, .14, 0, .24)
    for (const x of [-.5, .5]) {
      line(parts[0], dropButton.getPoints(16).map(point => [x + point.x, .13, .67 - point.y]), true)
      parts[0].children.at(-1).name = 'timer-cover-drop'
      circle(parts[0], x, .26, .045)
      parts[0].children.at(-1).name = 'timer-cover-indicator'
    }
    const bodyShape = roundedRect(2.2, 2.3, .24)
    bodyShape.holes.push(hole(1.92, 2.02, 0, 0, .10))
    solid(parts[1], bodyShape, .55, '#eaf2fc')
    const floor = new THREE.Group()
    floor.name = 'timer-housing-floor'
    solid(floor, roundedRect(1.92, 2.02, .10), .08, '#eaf2fc')
    parts[1].add(floor)
    const inlet = new THREE.Group()
    cylinder(inlet, .30, .70, 0, 0, 0, true)
    for (const y of [-.2, 0, .2]) circle(inlet, 0, 0, .31, y)
    inlet.rotation.x = Math.PI / 2
    inlet.position.set(0, .27, -1.45)
    parts[1].add(inlet)
    for (const x of [-.65, .65]) {
      const outlet = new THREE.Group()
      const outletHeight = .65 * .75
      cylinder(outlet, .27, outletHeight, 0, 0, 0, true)
      cylinderSides(outlet, .27, -outletHeight / 2, outletHeight / 2, 0, 0, [9, 7])
      thread(outlet, .278, -.17, .18, 3)
      circle(outlet, 0, 0, .28, -.2175)
      circle(outlet, 0, 0, .22, -outletHeight / 2 - .001)
      outlet.rotation.x = Math.PI / 2
      outlet.position.set(x, .27, 1.45)
      parts[2].add(outlet)
    }
    board(mainBoard, 1.85, 1.8)
    for (const [x, z] of [[-.77, -.75], [.77, -.75], [-.77, .75], [.77, .75]]) screw(screws, x, z)
    solid(battery, roundedRect(1.25, .78, .08), .18, '#f0f6fd')
    line(battery, [[-.46, .185, -.24], [-.46, .185, .24], [.46, .185, .24], [.46, .185, -.24]], true)
    solid(solarPanel, roundedRect(2.18, 2.28, .20), .06, '#fbfcfe')
    const solarCells = new THREE.Group()
    solarCells.position.y = .065
    solid(solarCells, roundedRect(1.94, 2.04, .12), .004, '#dbeaff')
    solarPanel.add(solarCells)
    for (const x of [-.48, 0, .48]) line(solarCells, [[x, .006, -.94], [x, .006, .94]])
    for (const z of [-.6, -.2, .2, .6]) line(solarCells, [[-.89, .006, z], [.89, .006, z]])
  }
  if (product === 'thermostat') {
  // Keep the original cover/frame/base indices for their localized callouts.
  const buttons = new THREE.Group(), lightBoard = new THREE.Group(), mainBoard = new THREE.Group(), screws = new THREE.Group()
  parts.push(buttons, lightBoard, mainBoard, screws)
  solid(parts[0], roundedRect(3.6, 2.65, .09), .11, '#fbfcfe')
  const screen = roundedRect(2.95, 1.65, .04)
  const display = new THREE.Group()
  display.position.set(0, .115, -.20)
  solid(display, screen, .006, '#edf4fc')
  parts[0].add(display)
  for (const x of [-1.05, -.35, .35, 1.05]) circle(parts[0], x, .98, .13)
  line(parts[0], [[-1.35, .135, -.92], [1.35, .135, -.92]])
  const frame = roundedRect(3.55, 2.6, .08)
  frame.holes.push(hole(2.9, 1.8))
  solid(parts[1], frame, .13, '#f4f8fe')
  for (const [x, z] of [[-1.57, -.98], [1.57, .98]]) circle(parts[1], x, z, .045, .14)
  line(parts[1], [[-1.48, .145, -.97], [-1.26, .145, -.97], [-1.26, .145, -.84], [-1.48, .145, -.84]], true)
  line(parts[1], [[1.26, .145, .84], [1.48, .145, .84], [1.48, .145, .97], [1.26, .145, .97]], true)
  const mount = roundedRect(3.4, 2.48, .055)
  mount.holes.push(hole(.88, .66))
  solid(parts[2], mount, .10, '#eaf2fc')
  for (const [x, z] of [[-1.40, -.96], [1.40, .96]]) circle(parts[2], x, z, .06, .11)
  for (const [x, z] of [[-.85, -.94], [.85, .94]]) {
    line(parts[2], [[x - .22, .115, z], [x + .22, .115, z]])
    line(parts[2], [[x - .22, .115, z - .045], [x + .22, .115, z - .045], [x + .22, .115, z + .045], [x - .22, .115, z + .045]], true)
  }
  line(parts[2], [[-1.28, .115, -.76], [1.28, .115, -.76], [1.28, .115, .76], [-1.28, .115, .76]], true)
  for (const x of [-1.05, -.35, .35, 1.05]) {
    cylinder(buttons, .13, .07, x, .02, .98)
    line(buttons, [[x - .035, .061, .98], [x + .035, .061, .98]])
  }
  solid(lightBoard, roundedRect(2.95, 1.72, .05), .035, '#f5f9ff')
  for (const x of [-1.16, -.58, 0, .58, 1.16]) {
    const led = new THREE.Group()
    led.position.set(x, .04, -.65)
    solid(led, roundedRect(.16, .11, .018), .025, '#e3efff')
    lightBoard.add(led)
  }
  line(lightBoard, [[-1.23, .041, -.48], [1.23, .041, -.48], [1.23, .041, .56], [.8, .041, .56]])
  solid(mainBoard, roundedRect(3.1, 2.12, .05), .055, '#eaf2fc')
  const chip = new THREE.Group()
  chip.position.set(-.22, .06, -.05)
  solid(chip, roundedRect(.7, .65, .035), .08, '#dbeaff')
  mainBoard.add(chip)
  for (const x of [-.45, -.30, -.15, 0]) {
    line(mainBoard, [[x, .061, -.48], [x, .061, -.68]])
    line(mainBoard, [[x, .061, .29], [x, .061, .49]])
  }
  for (const [x, z, width, height] of [[.85, -.55, .58, .25], [.88, .42, .4, .5], [-1, .53, .3, .23]]) {
    const component = new THREE.Group()
    component.position.set(x, .06, z)
    solid(component, roundedRect(width, height, .025), .055, '#f5f9ff')
    mainBoard.add(component)
  }
  line(mainBoard, [[-.57, .061, -.18], [-.95, .061, -.18], [-.95, .061, -.8], [.85, .061, -.8], [.85, .061, -.67]])
  line(mainBoard, [[.13, .061, .12], [.48, .061, .12], [.48, .061, .42], [.68, .061, .42]])
  line(mainBoard, [[-.22, .061, .49], [-.22, .061, .82], [-1, .061, .82], [-1, .061, .64]])
  for (const [x, z] of [[-1.42, -.91], [1.42, -.91], [-1.42, .91], [1.42, .91]]) {
    circle(mainBoard, x, z, .06, .057)
    // Seat the head on the PCB and keep the shaft inside the assembled housing.
    screw(screws, x, z, true)
  }
  }
  parts.forEach(part => root.add(part))
  const partKeys = product === 'thermostat' ? ['cover', 'frame', 'mount', 'buttons', 'light-board', 'main-board', 'screws']
    : product === 'trv' ? ['cover', 'body', 'mount', 'main-board', 'chip', 'inner-body', 'screws']
      : ['cover', 'body', 'mount', 'main-board', 'screws', 'battery', 'solar-panel']
  return { root, parts, partKeys, dispose() { geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose()) } }
}

function partOffsets(product, progress) {
  if (product === 'thermostat') return [.11 + progress * 1.85, -.12 + progress * .35, -.28 - progress * 1.8,
    .17 + progress * 1.5, .05 + progress * .95, -.18 - progress * .75, -.18 - progress * .20]
  if (product === 'trv') return [.4 + progress * 3.0, 0, -progress * 3.3,
    .15 + progress * 1.1, .22 + progress * 2.05, -progress * 2.7, .15 + progress * 1.5]
  return [.55 + progress * 1.9, 0, -progress * 1.2,
    .36 + progress * .75, .45 + progress * 1.35, .10, .70 + progress * 2.7]
}

function projectedPartBounds(partBoxes, offsets, modelScale, camera) {
  const bounds = new THREE.Box2()
  const point = new THREE.Vector3()
  partBoxes.forEach((box, index) => {
    for (const x of [box.min.x, box.max.x]) {
      for (const y of [box.min.y, box.max.y]) {
        for (const z of [box.min.z, box.max.z]) {
          point.set(x, y + offsets[index] * modelScale, z).applyMatrix4(camera.matrixWorldInverse)
          bounds.expandByPoint(new THREE.Vector2(point.x, point.y))
        }
      }
    }
  })
  return bounds
}

export default function ThermostatStructureScene({ product = 'thermostat', label, partLabels, fallback }) {
  const hostRef = useRef(null)
  const canvasRef = useRef(null)
  const labelsRef = useRef([])
  const leadersRef = useRef([])
  const fallbackRef = useRef(null)
  const [ready, setReady] = useState(false)
  const labelsKey = partLabels.join('\u0000')

  useEffect(() => {
    if (ready) return
    const host = hostRef.current
    const svg = fallbackRef.current?.querySelector('svg')
    if (!svg) return
    function layoutFallback() {
      const bounds = svg.getBBox()
      if (!bounds.width || !bounds.height) return
      const width = host.clientWidth, height = host.clientHeight
      const labels = [...host.querySelectorAll('.structure-fallback-label')]
      const labelWidth = Math.max(0, ...labels.map(element => element.offsetWidth))
      const halfHeight = fitStructureFrame(bounds.width, bounds.height, width, height, labelWidth)
      const scale = height / (halfHeight * 2)
      const productWidth = bounds.width * scale, productHeight = bounds.height * scale
      svg.setAttribute('viewBox', `${bounds.x - 2} ${bounds.y - 2} ${bounds.width + 4} ${bounds.height + 4}`)
      svg.style.width = `${productWidth + 4 * scale}px`
      svg.style.height = `${productHeight + 4 * scale}px`
      svg.style.left = `${width * STRUCTURE_PRODUCT_CENTER - productWidth / 2 - 2 * scale}px`
      svg.style.top = `${(height - productHeight) / 2 - 2 * scale}px`
      for (const element of labels) element.style.setProperty('--structure-leader-width', `${Math.max(0, width * STRUCTURE_PRODUCT_CENTER - productWidth / 2 - 8 - element.offsetWidth - 8)}px`)
      if (product === 'timer') {
        labels.forEach(element => element.style.removeProperty('top'))
        const hostTop = host.getBoundingClientRect().top
        const positions = labels.map(element => {
          const box = element.getBoundingClientRect()
          return { element, anchorY: box.top + box.height / 2 - hostTop, height: box.height }
        })
        for (const position of separateStructureLabels(positions, height)) position.element.style.setProperty('top', `${position.labelY}px`, 'important')
      }
    }
    const observer = new ResizeObserver(layoutFallback)
    observer.observe(host)
    layoutFallback()
    return () => observer.disconnect()
  }, [ready, product, labelsKey])

  useEffect(() => {
    setReady(false)
    const host = hostRef.current
    const section = host.closest('.structural-section') || host
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    let renderer
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true, antialias: true, preserveDrawingBuffer: true })
    } catch {
      host.dataset.renderer = 'fallback'
      return
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    renderer.setClearColor('#fbfcfe', 0)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    const scene = new THREE.Scene()
    const camera = new THREE.OrthographicCamera(-3.6, 3.6, 4.1, -4.1, .1, 50)
    camera.position.set(7, 9, 11)
    camera.lookAt(0, 0, 0)
    const model = createThermostat(product)
    const modelScale = product === 'thermostat' ? 1.3 : 1.5
    model.root.scale.setScalar(modelScale)
    scene.add(model.root)
    model.root.updateMatrixWorld(true)
    camera.updateMatrixWorld(true)
    const partBoxes = model.parts.map(part => new THREE.Box3().setFromObject(part))
    const endpointBounds = [0, 1].map(value => projectedPartBounds(partBoxes, partOffsets(product, value), modelScale, camera))
    const frameWidth = Math.max(...endpointBounds.map(bounds => bounds.max.x - bounds.min.x))
    const frameHeight = Math.max(...endpointBounds.map(bounds => bounds.max.y - bounds.min.y))
    let halfHeight = frameHeight / 2
    let aspect = 1
    let frame = 0
    let progress = 0, explosion = reduced.matches ? 1 : 0, visible = true, disposed = false

    function measure() {
      const viewport = window.innerHeight
      const sectionBounds = section.getBoundingClientRect()
      const bounds = sectionBounds.height < viewport - 1 ? sectionBounds : host.getBoundingClientRect()
      // Begin assembled when the section bottom reaches the viewport bottom.
      const completeTop = 0
      const travel = Math.max(1, viewport - bounds.height)
      progress = clamp((viewport - bounds.bottom) / travel, 0, 1)
      host.dataset.scrollTarget = bounds === sectionBounds ? 'section' : 'artwork'
      host.dataset.scrollProgress = progress.toFixed(4)
      host.dataset.completeTop = completeTop.toFixed(2)
      host.dataset.assembledTop = (viewport - bounds.height).toFixed(2)
    }
    function render() {
      const offsets = partOffsets(product, explosion)
      model.parts.forEach((part, index) => { part.position.y = offsets[index] })
      // Reserve the left column for labels and keep the assembly on the right golden-section line.
      const bounds = projectedPartBounds(partBoxes, offsets, modelScale, camera)
      const center = bounds.getCenter(new THREE.Vector2())
      camera.left = center.x - halfHeight * aspect * 2 * STRUCTURE_PRODUCT_CENTER
      camera.right = camera.left + halfHeight * aspect * 2
      camera.top = center.y + halfHeight
      camera.bottom = center.y - halfHeight
      camera.updateProjectionMatrix()
      host.dataset.framing = 'golden-section-callouts'
      host.dataset.projectedBounds = JSON.stringify({
        left: (bounds.min.x - camera.left) / (camera.right - camera.left),
        right: (bounds.max.x - camera.left) / (camera.right - camera.left),
        top: (camera.top - bounds.max.y) / (camera.top - camera.bottom),
        bottom: (camera.top - bounds.min.y) / (camera.top - camera.bottom),
      })
      renderer.render(scene, camera)
      host.dataset.explosion = explosion.toFixed(4)
      host.dataset.partOffsets = offsets.map(n => n.toFixed(4)).join(',')
      host.dataset.partCount = String(model.parts.length)
      host.dataset.partKeys = model.partKeys.join(',')
      host.dataset.modelScale = String(modelScale)
      host.dataset.outlineWidth = '1.5'
      const hostHeight = host.clientHeight
      const labelPositions = labelsRef.current.flatMap((element, index) => {
        if (!element) return []
        const partBounds = projectedPartBounds([partBoxes[index]], [offsets[index]], modelScale, camera)
        const anchorX = (partBounds.min.x - camera.left) / (camera.right - camera.left) * host.clientWidth
        const anchorY = (camera.top - (partBounds.min.y + partBounds.max.y) / 2) / (camera.top - camera.bottom) * hostHeight
        element.style.left = '8px'
        element.style.opacity = String(clamp((explosion - .16) / .35, 0, 1))
        return [{ element, index, anchorX, anchorY, height: element.offsetHeight }]
      })
      const arrangedLabels = product === 'timer' ? separateStructureLabels(labelPositions, hostHeight) : labelPositions
      for (const position of arrangedLabels) {
        const labelY = position.labelY ?? position.anchorY
        position.element.style.top = `${labelY}px`
        const start = 8 + position.element.offsetWidth + 4
        const leader = leadersRef.current[position.index]
        leader?.setAttribute('points', `${start},${labelY} ${position.anchorX - 2},${labelY}`)
        const redundantButtonLeader = product === 'thermostat' && position.index === 3
        if (leader) leader.style.opacity = redundantButtonLeader ? '0' : position.element.style.opacity
      }
    }
    function tick() {
      frame = 0
      if (disposed || !visible || document.hidden) return
      measure()
      explosion = reduced.matches ? 1 : progress
      render()
    }
    function requestRender() {
      if (!frame && visible && !document.hidden) frame = requestAnimationFrame(tick)
    }
    function onScroll() {
      measure()
      requestRender()
    }
    function resize() {
      const { width, height } = host.getBoundingClientRect()
      if (!width || !height) return
      renderer.setSize(width, height, false)
      aspect = width / height
      const labelWidth = Math.max(0, ...labelsRef.current.filter(Boolean).map(element => element.offsetWidth))
      halfHeight = fitStructureFrame(frameWidth, frameHeight, width, height, labelWidth)
      measure()
      requestRender()
    }
    function preferenceChanged() {
      measure()
      requestRender()
    }
    function visibilityChanged() {
      if (document.hidden) { cancelAnimationFrame(frame); frame = 0 }
      else { measure(); requestRender() }
    }
    function contextLost(event) {
      event.preventDefault()
      visible = false
      cancelAnimationFrame(frame)
      host.dataset.renderer = 'fallback'
      setReady(false)
    }
    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting
      if (visible) { measure(); requestRender() }
      else { cancelAnimationFrame(frame); frame = 0 }
    }, { rootMargin: '100px' })
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(host)
    resizeObserver.observe(section)
    labelsRef.current.filter(Boolean).forEach(element => resizeObserver.observe(element))
    observer.observe(host)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', visibilityChanged)
    reduced.addEventListener('change', preferenceChanged)
    canvasRef.current.addEventListener('webglcontextlost', contextLost)
    host.dataset.renderer = 'three'
    resize()
    setReady(true)
    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      observer.disconnect()
      resizeObserver.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', visibilityChanged)
      reduced.removeEventListener('change', preferenceChanged)
      canvasRef.current?.removeEventListener('webglcontextlost', contextLost)
      model.dispose()
      renderer.dispose()
    }
  }, [product, labelsKey])

  return <div id="structure-product-diagram" ref={hostRef} className="structure-drawing" data-product={product} data-renderer="pending" data-explosion="0" data-scroll-progress="0">
    <canvas ref={canvasRef} className="structure-canvas" role="img" aria-label={label} aria-hidden={!ready} />
    <div ref={fallbackRef} className="structure-fallback" hidden={ready}>{fallback}</div>
    <div className="structure-part-labels" aria-hidden="true" hidden={!ready}>
      <svg className="structure-label-leaders" width="100%" height="100%">{partLabels.map((_, index) => <polyline key={index} ref={element => { leadersRef.current[index] = element }} />)}</svg>
      {partLabels.map((partLabel, index) => <span className="structure-part-label" key={index} ref={element => { labelsRef.current[index] = element }}>{partLabel}</span>)}
    </div>
    {!ready && <div className="structure-part-labels" aria-hidden="true">{partLabels.map((partLabel, index) => <span key={index} className="structure-part-label structure-fallback-label" style={{ left: '2%', top: `${20 + index * 30}%` }}>{partLabel}</span>)}</div>}
  </div>
}
